import React, {useState} from "react";
import {
  AButton,
  ADotLoader,
  AList,
  AListItem,
  AListItemSubtitle,
  AListItemTitle
} from "@cisco-sbg-ui/magna-react";
import {t} from "i18next";
import {Trans} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import EmptyContent from "components/EmptyContent";
import MarkdownEditor from "components/MarkdownEditor";
import {postIncidentNote} from "store/features/incidentDetailSlice";
import {
  selectIncidentDetailId,
  selectIncidentNotes,
  selectIncidentNoteSaving,
  selectIncidentNotesLoading,
  selectIncidentNotesTotalCount
} from "store/selectors";
import {sendIncidentDetailsAddNoteTelemetry} from "api/telemetry/incidentsTelemetrics";
import {getShortId} from "utils/misc";
import theme from "theme/index.scss";
import styles from "./Worklog.module.scss";

const NOTE_CHARACTERS_LIMIT = 2000;

const NotesPanel = ({isEditorOpen, handleEditorOpen, limit, sortBy}) => {
  const [value, setValue] = useState("");
  const loadingIncident = useSelector(selectIncidentNotesLoading);
  const notes = useSelector(selectIncidentNotes);
  const notesTotalCount = useSelector(selectIncidentNotesTotalCount);
  const isSavingNote = useSelector(selectIncidentNoteSaving);
  const isLoadingNotes = useSelector(selectIncidentNotesLoading);
  const dispatch = useDispatch();

  const incidentId = useSelector(selectIncidentDetailId);
  const telemetryButtonClick = (buttonName, callback) => {
    sendIncidentDetailsAddNoteTelemetry(getShortId(incidentId), buttonName);
    callback();
  };

  const atCharLimit = value.length > NOTE_CHARACTERS_LIMIT;

  // TODO create separate TelemetryButton component
  const TelemetryButton = ({name, callback, ...rest}) => {
    return (
      <AButton
        medium
        onClick={() => telemetryButtonClick(name, callback)}
        {...rest}>
        {name}
      </AButton>
    );
  };
  return (
    <div data-testid={"NotesPanel-Container"}>
      <MarkdownEditor
        disabled={isSavingNote}
        active={isEditorOpen}
        value={value}
        onChange={(newValue) => setValue(newValue)}
        hasValidationError={atCharLimit}
      />
      {isEditorOpen && (
        <div className={"d-flex align-end flex-column"}>
          <div className={"d-flex align-start mr-1"}>
            <p
              style={{
                color: atCharLimit && theme.grayTextColor
              }}>
              <Trans
                i18nKey="incidentDetail.worklogTab.noteInput.lengthHint"
                values={{
                  current: value?.length ?? 0,
                  limit: NOTE_CHARACTERS_LIMIT
                }}
                components={{
                  curr: (
                    <span
                      style={{
                        fontWeight: atCharLimit ? "bold" : "initial",
                        color: atCharLimit && theme.grayButtonHoverText
                      }}
                    />
                  )
                }}
              />
            </p>
          </div>
          <div className={"mb-5"}>
            <TelemetryButton
              data-testid={"NotesPanel-Button-add-note-close"}
              name={t("misc.close")}
              callback={() => {
                handleEditorOpen(false);
                setValue("");
              }}
              className="mt-0 ml-2"
              tertiary
              medium
              disabled={isSavingNote}
            />
            <TelemetryButton
              data-testid={"NotesPanel-Button-add-note-save"}
              className="mt-0 ml-2"
              callback={() => {
                dispatch(
                  postIncidentNote({
                    limit,
                    sortBy,
                    content: value
                  })
                );
                /**
                 * @todo Only perform these clearing operations on success
                 */
                setValue("");
                handleEditorOpen(false);
              }}
              name={t("incidentDetail.detail.description.saveMarkdown")}
              disabled={!value || atCharLimit || isSavingNote}
              primary
              medium
            />
          </div>
        </div>
      )}
      {loadingIncident || isLoadingNotes ? (
        <ADotLoader className="d-block mx-auto" />
      ) : (
        <div data-testid={"NotesPanel-Container"}>
          {notesTotalCount === 0 ? (
            <EmptyContent
              name={t("incidentDetail.worklogTab.notesTab").toLowerCase()}
            />
          ) : (
            <AList
              hoverable={false}
              style={{
                overflow: "auto",
                minHeight: "400px",
                border: "none",
                backgroundColor: "initial"
              }}
              data-testid={"NotesPanel-List"}>
              {notes.map((note) => (
                <AListItem
                  data-testid="NotesPanel-ListItem"
                  key={note.id}
                  style={{
                    flexDirection: "column",
                    alignItems: "start",
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px"
                  }}
                  className={styles.listItem}>
                  <div className="d-flex align-center pb-2">
                    <AListItemTitle>
                      {t("incidentDetail.worklogTab.notesPanel.createdBy", {
                        author: note.owner
                      })}
                    </AListItemTitle>
                    <span>|</span>
                    <AListItemSubtitle>{note.timestamp}</AListItemSubtitle>
                  </div>
                  <MarkdownEditor
                    active={false}
                    minPreviewHeight={10}
                    value={note.content}
                  />
                </AListItem>
              ))}
            </AList>
          )}
        </div>
      )}
    </div>
  );
};

export default NotesPanel;
